/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import { get } from "../../../utils/api";
import { useUser } from "../../../context/AuthContext";

const currentYear = new Date().getFullYear();
const monthOptions = [
  { id: 1, label: "Jan" },
  { id: 2, label: "Feb" },
  { id: 3, label: "Mar" },
  { id: 4, label: "Apr" },
  { id: 5, label: "May" },
  { id: 6, label: "Jun" },
  { id: 7, label: "Jul" },
  { id: 8, label: "Aug" },
  { id: 9, label: "Sep" },
  { id: 10, label: "Oct" },
  { id: 11, label: "Nov" },
  { id: 12, label: "Dec" },
];

const buildMonthYears = () => {
  const years = [];
  for (let year = 2023; year <= currentYear; year += 1) {
    years.push(year);
  }
  return years;
};

const emptyTotals = {
  naturalGas: "",
  coal: "",
  petroleumGasoline: "",
  diesel: "",
  fuelOil: "",
  lpg: "",
  propane: "",
  heatingAndCooling: "",
  cng: "",
  steam: "",
  electricity: "",
  tAndD: "",
  resaleQuantity: "",
};

const buildTotalsFromInitialData = (initialData) => ({
  naturalGas: initialData?.purchasedFuels?.naturalGas ?? "",
  coal: initialData?.purchasedFuels?.coal ?? "",
  petroleumGasoline:
    initialData?.purchasedFuels?.petroleumGasoline ??
    initialData?.purchasedFuels?.petrolOrGasoline ??
    "",
  diesel: initialData?.purchasedFuels?.diesel ?? "",
  fuelOil: initialData?.purchasedFuels?.fuelOil ?? "",
  lpg: initialData?.purchasedFuels?.lpg ?? "",
  propane: initialData?.purchasedFuels?.propane ?? "",
  heatingAndCooling:
    initialData?.purchasedFuels?.heatingAndCooling ??
    initialData?.purchasedFuels?.["Heating and Cooling"] ??
    "",
  cng: initialData?.purchasedFuels?.cng ?? "",
  steam: initialData?.purchasedFuels?.steam ?? "",
  electricity: initialData?.purchasedElectricity?.electricity ?? "",
  tAndD: initialData?.transmissionAndDistributionLosses?.electricity ?? "",
  resaleQuantity:
    initialData?.purchasedElectricityForResale?.resoldQuantity ?? "",
});

const Form = ({ isOpen, onClose, onSubmit, header, initialData, isEditing }) => {
  const { user } = useUser();
  const initialFormData = useMemo(
    () => ({
      date: "",
      consumptionPeriod: {
        month: initialData?.consumptionPeriod?.month || "",
        year: initialData?.consumptionPeriod?.year || "",
      },
      purchasedFuels: {
        naturalGas: initialData?.purchasedFuels?.naturalGas || "",
        coal: initialData?.purchasedFuels?.coal || "",
        petroleumGasoline:
          initialData?.purchasedFuels?.petroleumGasoline || "",
        diesel: initialData?.purchasedFuels?.diesel || "",
        fuelOil: initialData?.purchasedFuels?.fuelOil || "",
        lpg: initialData?.purchasedFuels?.lpg || "",
        propane: initialData?.purchasedFuels?.propane || "",
        heatingAndCooling:
          initialData?.purchasedFuels?.heatingAndCooling || "",
        cng: initialData?.purchasedFuels?.cng || "",
        steam: initialData?.purchasedFuels?.steam || "",
      },
      purchasedElectricity: {
        electricity: initialData?.purchasedElectricity?.electricity || "",
      },
      transmissionAndDistributionLosses: {
        electricity:
          initialData?.transmissionAndDistributionLosses?.electricity || "",
      },
      purchasedElectricityForResale: {
        purchased:
          initialData?.purchasedElectricityForResale?.purchased || false,
        resoldQuantity:
          initialData?.purchasedElectricityForResale?.resoldQuantity || "",
        unit: initialData?.purchasedElectricityForResale?.unit || "KWH",
      },
      remark: initialData?.remark || "",
      editRemarkReason: initialData?.editRemarkReason || "",
      documents: initialData?.documents || [],
    }),
    [initialData]
  );

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedFilesSnapshot, setUploadedFilesSnapshot] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [fetchedTotals, setFetchedTotals] = useState(emptyTotals);
  const [expanded, setExpanded] = useState({
    fuels: false,
    electricity: false,
    td: false,
  });

  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
    setUploadedFilesSnapshot(initialData?.documents || []);
    setErrors({});
    setFetchedTotals(
      isEditing && initialData?.purchasedFuels
        ? buildTotalsFromInitialData(initialData)
        : emptyTotals
    );
    setExpanded({
      fuels: false,
      electricity: false,
      td: false,
    });
  }, [isOpen, initialData]);

  const makeDateFromMonthYear = (month, year) => {
    if (!month || !year) return "";
    return `${year}-${String(month).padStart(2, "0")}-01`;
  };

  const fetchStationaryTotals = async (monthArg, yearArg) => {
    const month = monthArg ?? formData?.consumptionPeriod?.month;
    const year = yearArg ?? formData?.consumptionPeriod?.year;
    if (!month || !year) return;

    try {
      const query = new URLSearchParams({ month, year }).toString();
      const res = await get(`/scope3/fuelEnergyActivities/stationary-combustion-total?${query}`);
      const stationary = res?.data?.data?.fuelQuantity?.stationaryCombustion || {};
      const purchasedFuel = res?.data?.data?.fuelQuantity?.purchasedFuel || {};
      const mobile = res?.data?.data?.fuelQuantity?.mobileCombustion || {};
      const heatingAndCooling =
        res?.data?.data?.fuelQuantity?.["Heating and Cooling"] || {};
      const steam = res?.data?.data?.fuelQuantity?.Steam || {};
      const electricityConsumed = res?.data?.data?.electricityConsumed || {};
      const electricityTotal = electricityConsumed?.total?.quantity ?? "";
      const onRoad = mobile?.onRoad || {};
      const offRoad = mobile?.offRoad || {};
      const petrolTotal =
        (stationary?.petrolOrGasoline ?? 0) +
        (onRoad?.petrol?.quantity ?? 0) +
        (offRoad?.petrol?.quantity ?? 0);
      const dieselTotal =
        (stationary?.diesel ?? 0) +
        (onRoad?.disel?.quantity ?? 0) +
        (offRoad?.disel?.quantity ?? 0);
      const cngTotal =
        (onRoad?.CNG?.quantity ?? 0) + (offRoad?.CNG?.quantity ?? 0);
      setFetchedTotals({
        naturalGas: purchasedFuel?.naturalGas ?? stationary?.naturalGas ?? "",
        coal: purchasedFuel?.coal ?? stationary?.coal ?? "",
        petroleumGasoline:
          purchasedFuel?.petrolOrGasoline ?? purchasedFuel?.petroleumGasoline ?? petrolTotal,
        diesel: purchasedFuel?.diesel ?? stationary?.diesel ?? dieselTotal,
        fuelOil: purchasedFuel?.fuelOil ?? stationary?.fuelOil ?? "",
        lpg: purchasedFuel?.lpg ?? stationary?.lpg ?? "",
        propane: purchasedFuel?.propane ?? stationary?.propane ?? "",
        heatingAndCooling:
          purchasedFuel?.["Heating and Cooling"] ??
          purchasedFuel?.heatingAndCooling ??
          heatingAndCooling?.total?.quantity ??
          heatingAndCooling?.consumptionByFacility ??
          heatingAndCooling?.consumptionByEquipment ??
          "",
        cng: purchasedFuel?.cng ?? cngTotal,
        steam:
          purchasedFuel?.steam ??
          steam?.total?.quantity ??
          steam?.consumptionByFacility ??
          steam?.consumptionByEquipment ??
          "",
        electricity: electricityTotal,
        tAndD: electricityTotal,
        resaleQuantity: formData?.purchasedElectricityForResale?.resoldQuantity || "",
      });
      setFormData((prev) => ({
        ...prev,
        date: makeDateFromMonthYear(month, year),
        purchasedFuels: {
          ...prev.purchasedFuels,
          naturalGas: stationary?.naturalGas ?? "",
          coal: stationary?.coal ?? "",
          petroleumGasoline: petrolTotal,
          diesel: dieselTotal,
          fuelOil: stationary?.fuelOil ?? "",
          lpg: stationary?.lpg ?? "",
          propane: stationary?.propane ?? "",
          heatingAndCooling:
            heatingAndCooling?.total?.quantity ??
            heatingAndCooling?.consumptionByFacility ??
            heatingAndCooling?.consumptionByEquipment ??
            "",
          cng: cngTotal,
          steam:
            steam?.total?.quantity ??
            steam?.consumptionByFacility ??
            steam?.consumptionByEquipment ??
            "",
        },
        purchasedElectricity: {
          ...prev.purchasedElectricity,
          electricity: electricityTotal,
        },
        transmissionAndDistributionLosses: {
          ...prev.transmissionAndDistributionLosses,
          electricity: electricityTotal,
        },
      }));
    } catch (error) {
      console.error("Error fetching stationary combustion totals:", error);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const month = formData?.consumptionPeriod?.month;
    const year = formData?.consumptionPeriod?.year;
    if (month && year) {
      fetchStationaryTotals(month, year);
    }
  }, [isOpen, formData?.consumptionPeriod?.month, formData?.consumptionPeriod?.year]);

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFetchedTotals(emptyTotals);
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    setUploadedFilesSnapshot([]);
    onClose();
  };

  const getComparableState = (data, files) => {
    if (!data) return data;
    return {
      consumptionPeriod: data.consumptionPeriod,
      purchasedElectricityForResale:
        data.purchasedElectricityForResale,
      documents: files || [],
    };
  };

  const isFormChanged = () =>
    JSON.stringify(getComparableState(formData, uploadedFiles)) !==
    JSON.stringify(getComparableState(formDataSnapshot, uploadedFilesSnapshot));

  const handleClose = () => {
    if (!isFormChanged()) handleExitAgree();
    else setEditDialog(true);
  };

  const handleChange = (path) => (event) => {
    const { value } = event.target;
    let nextMonth = null;
    let nextYear = null;
    setFormData((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      if (parts.length === 2) {
        next[parts[0]] = { ...next[parts[0]], [parts[1]]: value };
      } else {
        next[path] = value;
      }
      nextMonth = next.consumptionPeriod?.month;
      nextYear = next.consumptionPeriod?.year;
      next.date = makeDateFromMonthYear(
        nextMonth,
        nextYear
      );
      return next;
    });
    setErrors((prev) => ({ ...prev, [path]: "" }));

    if (
      (path === "consumptionPeriod.month" || path === "consumptionPeriod.year") &&
      nextMonth &&
      nextYear
    ) {
      fetchStationaryTotals(nextMonth, nextYear);
    }
  };

  const validateFormData = () => {
    const newErrors = {};
    if (!formData?.consumptionPeriod?.month) {
      newErrors["consumptionPeriod.month"] = "Consumption month is required.";
    }
    if (!formData?.consumptionPeriod?.year) {
      newErrors["consumptionPeriod.year"] = "Consumption year is required.";
    }
    if (
      formData?.purchasedElectricityForResale?.purchased &&
      (formData?.purchasedElectricityForResale?.resoldQuantity === "" ||
        formData?.purchasedElectricityForResale?.resoldQuantity === null ||
        formData?.purchasedElectricityForResale?.resoldQuantity === undefined)
    ) {
      newErrors["purchasedElectricityForResale.resoldQuantity"] =
        "Purchased Electricity Resold Quantity is required.";
    }
    if (
      formData?.purchasedElectricityForResale?.purchased &&
      formData?.purchasedElectricityForResale?.resoldQuantity !== "" &&
      (!Number.isFinite(
        Number(formData?.purchasedElectricityForResale?.resoldQuantity)
      ) || Number(formData?.purchasedElectricityForResale?.resoldQuantity) <= 0)
    ) {
      newErrors["purchasedElectricityForResale.resoldQuantity"] =
        "Purchased Electricity Resold Quantity must be greater than 0.";
    }
    if (user?.endUser && isEditing && !formData?.editRemarkReason?.trim()) {
      newErrors.editRemarkReason = "Edit Remark Reason is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;
    const payload = {
      ...formData,
      date: makeDateFromMonthYear(
        formData?.consumptionPeriod?.month,
        formData?.consumptionPeriod?.year
      ),
      purchasedFuels: {
        ...formData?.purchasedFuels,
        unit: "MT",
      },
      purchasedElectricity: {
        ...formData?.purchasedElectricity,
        electricity:
          fetchedTotals?.electricity ??
          formData?.purchasedElectricity?.electricity ??
          "",
        unit: "KWH",
      },
      transmissionAndDistributionLosses: {
        ...formData?.transmissionAndDistributionLosses,
        unit: "KWH",
      },
      purchasedElectricityForResale: {
        ...formData?.purchasedElectricityForResale,
        unit: formData?.purchasedElectricityForResale?.unit || "KWH",
      },
      remark: formData?.remark || "",
      documents: uploadedFiles,
    };
    if (user?.endUser && isEditing) {
      payload.requestStatus = "edit";
      payload.requestMode = true;
      payload.approvalStatus = "Pending";
      payload.editRemarkReason = formData.editRemarkReason.trim();
    }
    onSubmit(
      payload,
      isEditing
    );
    setUploadedFiles([]);
  };

  const hasSelectedConsumptionPeriod =
    Boolean(formData?.consumptionPeriod?.month) &&
    Boolean(formData?.consumptionPeriod?.year);

  const renderReadOnlyValue = (value) => (
    <TextField
      size="small"
      fullWidth
      value={
        !hasSelectedConsumptionPeriod &&
        (value === "" || value === null || value === undefined)
          ? "Autofetched"
          : value === "" || value === null || value === undefined
          ? 0
          : value
      }
      disabled
      sx={{
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#f3f4f6",
        },
        "& .MuiInputBase-input.Mui-disabled": {
          WebkitTextFillColor: "#6b7280",
          color: "#6b7280",
        },
        "& .MuiInputBase-root.Mui-disabled": {
          backgroundColor: "#f3f4f6",
          cursor: "not-allowed",
        },
      }}
    />
  );

  const renderNumberField = (label, valueKey, unit = "Kg") => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          size="small"
          fullWidth
          value={
            !hasSelectedConsumptionPeriod &&
            (fetchedTotals[valueKey] === "" ||
              fetchedTotals[valueKey] === null ||
              fetchedTotals[valueKey] === undefined)
              ? "Autofetched"
              : fetchedTotals[valueKey] === "" ||
            fetchedTotals[valueKey] === null ||
            fetchedTotals[valueKey] === undefined
              ? 0
              : fetchedTotals[valueKey]
          }
          disabled
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f3f4f6",
            },
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#6b7280",
              color: "#6b7280",
            },
            "& .MuiInputBase-root.Mui-disabled": {
              backgroundColor: "#f3f4f6",
              cursor: "not-allowed",
            },
          }}
        />
        <TextField
          size="small"
          sx={{
            width: "90px",
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f3f4f6",
            },
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#6b7280",
              color: "#6b7280",
            },
            "& .MuiInputBase-root.Mui-disabled": {
              backgroundColor: "#f3f4f6",
              cursor: "not-allowed",
            },
          }}
          value={unit}
          disabled
        />
      </Box>
    </Box>
  );

  const handleAccordionChange = (panel) => (_, nextExpanded) => {
    setExpanded((prev) => ({
      ...prev,
      [panel]: nextExpanded,
    }));
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        display="flex"
        gap={3}
        sx={{
          width: "26.5rem",
          maxWidth: "100%",
          height: "100vh",
          justifyContent: "space-between",
          backgroundColor: "transparent",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            Add Entry
          </Typography>

          <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Consumption Month
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                select
                size="small"
                fullWidth
                value={formData?.consumptionPeriod?.month || ""}
                onChange={handleChange("consumptionPeriod.month")}
                error={Boolean(errors["consumptionPeriod.month"])}
                helperText={errors["consumptionPeriod.month"] || ""}
                FormHelperTextProps={{ sx: { mx: 0 } }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Month
                </MenuItem>
                {monthOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                fullWidth
                value={formData?.consumptionPeriod?.year || ""}
                onChange={handleChange("consumptionPeriod.year")}
                error={Boolean(errors["consumptionPeriod.year"])}
                helperText={errors["consumptionPeriod.year"] || ""}
                FormHelperTextProps={{ sx: { mx: 0 } }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Year
                </MenuItem>
                {buildMonthYears().map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Accordion
            expanded={expanded.fuels}
            onChange={handleAccordionChange("fuels")}
            disableGutters
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              borderBottom: "1px solid #9ca3af",
              backgroundColor: "transparent",
              mb: 0,
            }}
          >
            <AccordionSummary
              expandIcon={<ArrowDropDownIcon />}
              sx={{ px: 0 }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Purchased Fuels
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              {renderNumberField("Natural Gas", "naturalGas")}
              {renderNumberField("Coal", "coal")}
              {renderNumberField("Petrol", "petroleumGasoline")}
              {renderNumberField("Diesel", "diesel")}
              {renderNumberField("Fuel Oil", "fuelOil")}
              {renderNumberField("LPG", "lpg")}
              {renderNumberField("Propane", "propane")}
              {renderNumberField("CNG", "cng")}
              {renderNumberField("Heating and Cooling", "heatingAndCooling", "KWH")}
              {renderNumberField("Steam", "steam", "KWH")}
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.electricity}
            onChange={handleAccordionChange("electricity")}
            disableGutters
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              borderBottom: "1px solid #9ca3af",
              backgroundColor: "transparent",
            }}
          >
            <AccordionSummary
              expandIcon={<ArrowDropDownIcon />}
              sx={{ px: 0 }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Purchased Electricity
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Electricity
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {renderReadOnlyValue(fetchedTotals.electricity)}
                  <TextField
                    size="small"
                    sx={{
                      width: "90px",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f3f4f6",
                      },
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#6b7280",
                        color: "#6b7280",
                      },
                      "& .MuiInputBase-root.Mui-disabled": {
                        backgroundColor: "#f3f4f6",
                        cursor: "not-allowed",
                      },
                    }}
                    value="KWH"
                    disabled
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.td}
            onChange={handleAccordionChange("td")}
            disableGutters
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              borderBottom: "1px solid #9ca3af",
              backgroundColor: "transparent",
            }}
          >
            <AccordionSummary
              expandIcon={<ArrowDropDownIcon />}
              sx={{ px: 0 }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Transmission and Distribution Losses
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Electricity
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {renderReadOnlyValue(fetchedTotals.tAndD)}
                  <TextField
                    size="small"
                    sx={{
                      width: "90px",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f3f4f6",
                      },
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#6b7280",
                        color: "#6b7280",
                      },
                      "& .MuiInputBase-root.Mui-disabled": {
                        backgroundColor: "#f3f4f6",
                        cursor: "not-allowed",
                      },
                    }}
                    value="KWH"
                    disabled
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
            Purchased Electricity for Resale
          </Typography> */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Have you purchased electricity for Resale this month?
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
              <label>
                <input
                  type="radio"
                  checked={Boolean(formData?.purchasedElectricityForResale?.purchased)}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      purchasedElectricityForResale: {
                        ...prev.purchasedElectricityForResale,
                        purchased: true,
                      },
                    }))
                  }
                />{" "}
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  checked={!formData?.purchasedElectricityForResale?.purchased}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      purchasedElectricityForResale: {
                        ...prev.purchasedElectricityForResale,
                        purchased: false,
                        resoldQuantity: "",
                      },
                    }))
                  }
                />{" "}
                No
              </label>
            </Box>
            {formData?.purchasedElectricityForResale?.purchased && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Purchased Electricity Resold Quantity
                </Typography>
                <Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <TextField
                      type="text"
                      size="small"
                      fullWidth
                      inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.]?[0-9]*" }}
                      value={formData?.purchasedElectricityForResale?.resoldQuantity ?? ""}
                      onChange={(e) => {
                        const { value } = e.target;
                        if (!/^\d*\.?\d*$/.test(value)) return;
                        setFormData((prev) => ({
                          ...prev,
                          purchasedElectricityForResale: {
                            ...prev.purchasedElectricityForResale,
                            resoldQuantity: value,
                          },
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          "purchasedElectricityForResale.resoldQuantity": "",
                        }));
                      }}
                      placeholder="Enter Resold Quantity"
                      error={Boolean(
                        errors["purchasedElectricityForResale.resoldQuantity"]
                      )}
                    />
                    <TextField
                      size="small"
                      sx={{ width: "90px" }}
                      value={formData?.purchasedElectricityForResale?.unit || "KWH"}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          purchasedElectricityForResale: {
                            ...prev.purchasedElectricityForResale,
                            unit: e.target.value,
                          },
                        }))
                      }
                    />
                  </Box>
                  {errors["purchasedElectricityForResale.resoldQuantity"] && (
                    <Typography color="error" sx={{ mt: 0.5, fontSize: "0.75rem", lineHeight: 1.66 }}>
                      {errors["purchasedElectricityForResale.resoldQuantity"]}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1, mb: 0.5 }}>
              {formatTitleWithOptional("Remark (optional)")}
            </Typography>
            <TextField
              type="text"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Enter remark"
              value={formData?.remark || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  remark: e.target.value,
                }))
              }
              helperText=""
              FormHelperTextProps={{
                sx: { ml: 0, mr: 0, fontSize: "0.875rem" },
              }}
            />
          </Box>

          {user?.endUser && isEditing && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1, mb: 0.5 }}>
                Edit Remark Reason
              </Typography>
              <TextField
                size="small"
                fullWidth
                required
                multiline
                minRows={2}
                placeholder="Additional edit remarks"
                value={formData.editRemarkReason || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    editRemarkReason: e.target.value,
                  }))
                }
                error={Boolean(errors.editRemarkReason)}
                helperText={errors.editRemarkReason || ""}
                FormHelperTextProps={{
                  sx: { ml: 0, mr: 0 },
                }}
              />
            </Box>
          )}

          <form>
            <Box sx={{ mt: 2 }}>
              <FileField
                title={formatTitleWithOptional("Upload Documents (optional)")}
                name="document"
                errors={errors.document}
                fileUrls={uploadedFiles}
                progress={uploadProgress.document || []}
                {...{ setUploadedFiles, setUploadProgress, setErrors }}
              />
            </Box>
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none", mt: 2 }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;
